import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useImperativeHandle,
  useRef,
} from "react";
import { retryDynamicImport } from "@/utils/lazy-with-retry";

interface PdfGeneratorProps {
  onSave: (blob: any) => void;
  children: ReactNode;
}

/**
 * `This component is used to generate a pdf from a react component`
 *
 * @param onSave - The function to call when the pdf is generated @returns (blob: any) => void
 * @param children - The react component to generate the pdf from @returns ReactNode
 * @param ref - The ref to the component @returns { generatePdf: (name?: string) => void }
 *
 * @example
 * const pdfRef = useRef<{ generatePdf: (name?: string) => void }>(null);
 *
 * <PdfGenerator onSave={() => {}} ref={pdfRef}>
 *  <div>Hello</div>
 * </PdfGenerator>
 * pdfRef.current?.generatePdf("abc.pdf");
 *
 * @author Rajkumar Rathod
 */
export const PdfGenerator = forwardRef(
  (
    { onSave, children }: PdfGeneratorProps,
    ref: ForwardedRef<{ generatePdf: () => void }>
  ) => {
    const contentRef = useRef();

    /**
     * To Expose the generatePdf method To Higher order component
     *
     * @param ref
     */
    useImperativeHandle(ref, () => ({
      generatePdf: (name?: string) => {
        setTimeout(() => {
          if (!contentRef.current) return;

          // html2pdf bundles html2canvas + jsPDF; load it on demand, not with the page.
          void retryDynamicImport(() => import("html2pdf.js")).then(
            ({ default: html2pdf }) =>
              html2pdf()
                .set({
                  margin: 0.2,
                  filename: name ?? "report.pdf",
                  image: { type: "jpeg", quality: 0.98 },
                  html2canvas: { scale: 2 },
                  jsPDF: {
                    unit: "in",
                    format: "letter",
                    orientation: "portrait",
                  },
                })
                .from(contentRef.current)
                .outputPdf("blob")
                .then((blob: Blob) => {
                  onSave(blob);
                })
          );
        }, 0);
      },
    }));

    return (
      <div style={{ display: "none", zIndex: -1, visibility: "hidden" }}>
        <div ref={contentRef as any}>{children}</div>
      </div>
    );
  }
);
